import React, { useEffect, useState, useMemo } from "react";
import api from "../api/axios";
import { LoadingCard } from "../components/LoadingSpinner";

function Avatar({ name, size = 42 }) {
  const initials = (name || "?")
    .split(" ")
    .map((x) => x[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: 10,
        background: "linear-gradient(135deg, rgba(130,200,255,0.24), rgba(47,199,255,0.15))",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        fontSize: size / 2.5,
        color: "#021322",
      }}
    >
      {initials}
    </div>
  );
}

export default function Leaderboard() {
  const [certs, setCerts] = useState([]);
  const [quiz, setQuiz] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pagination for ranks 4+
  const [page, setPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [cRes, qRes] = await Promise.all([
          api.get("/certs/all"),
          api.get("/quiz/all"),
        ]);

        setCerts(cRes.data || []);
        setQuiz(qRes.data || []);
      } catch (err) {
        console.error("Failed to load leaderboard data:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Build leaderboard dataset
  const rows = useMemo(() => {
    const map = new Map();

    certs
      .filter((c) => c.status === "Completed")
      .forEach((c) => {
        if (!map.has(c.employeeId)) {
          map.set(c.employeeId, {
            employeeId: c.employeeId,
            name: c.name,
            location: c.location,
            certPoints: 0,
            quizPoints: 0,
          });
        }
        map.get(c.employeeId).certPoints += c.points || 0;
      });

    quiz.forEach((q) => {
      if (!map.has(q.employeeId)) {
        map.set(q.employeeId, {
          employeeId: q.employeeId,
          name: q.name,
          location: q.location,
          certPoints: 0,
          quizPoints: 0,
        });
      }
      map.get(q.employeeId).quizPoints += q.points || 0;
    });

    return Array.from(map.values())
      .map((p) => ({
        ...p,
        totalXP: (p.certPoints || 0) + (p.quizPoints || 0),
      }))
      .sort((a, b) => b.totalXP - a.totalXP);
  }, [certs, quiz]);

  // Pagination for 4th onwards
  const totalPages = Math.ceil((rows.length - 3) / pageSize);

  const paginatedRows = rows.slice(3 + (page - 1) * pageSize, 3 + page * pageSize);

  // Show loading state while data is being fetched
  if (loading) {
    return (
      <div className="container-fluid d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <LoadingCard message="Loading leaderboard..." />
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div style={cardContainer}>
        <h4 style={titleStyle}>Global Leaderboard</h4>

  {/* ---------------- TOP 3 PODIUM (RANK LABEL ON TOP + MEDALS) ---------------- */}
<div
  style={{
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-end",
    gap: 35,
    marginBottom: 45,
    marginTop: 25,
  }}
>
  {rows.slice(0, 3).map((user, idx) => {
    const rank = idx + 1;

    const styles = {
      1: { 
        border: "#ffd700", 
        glow: "#ffd70066", 
        label: "1st 🥇", 
        lift: -25 
      },
      2: { 
        border: "#c0c0c0", 
        glow: "#c0c0c055", 
        label: "2nd 🥈", 
        lift: 0 
      },
      3: { 
        border: "#cd7f32", 
        glow: "#cd7f3255", 
        label: "3rd 🥉", 
        lift: 5 
      },
    };

    return (
      <div
        key={user.employeeId}
        style={{
          width: 250,
          padding: 22,
          borderRadius: 20,
          background: "rgba(255,255,255,0.05)",
          border: `2px solid ${styles[rank].border}`,
          boxShadow: `0 0 18px ${styles[rank].glow}`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          transform: `translateY(${styles[rank].lift}px)`,
          transition: "0.3s",
          position: "relative",
        }}
      >
        {/* Rank label on top */}
        <div
          style={{
            position: "absolute",
            top: -18,
            padding: "4px 12px",
            borderRadius: 14,
            background: "rgba(3,19,40,0.9)",
            border: `1px solid ${styles[rank].border}`,
            color: styles[rank].border,
            fontWeight: 800,
            fontSize: 15,
            backdropFilter: "blur(4px)",
          }}
        >
          {styles[rank].label}
        </div>

        {/* Avatar */}
        <Avatar name={user.name} size={65} />

        {/* Name */}
        <div style={{ marginTop: 12, fontWeight: 700, color: "#eaf6ff", fontSize: 18 }}>
          {user.name}
        </div>

        {/* Location */}
        <div style={{ fontSize: 13, color: "#8ecbff", marginBottom: 10 }}>
          {user.location}
        </div>

        {/* XP */}
        <div
          style={{
            fontSize: 24,
            fontWeight: 800,
            color: "#eaf6ff",
          }}
        >
          {String(user.totalXP).padStart(4, "0")} XP
        </div>

        {/* Cert/Quiz Breakup */}
        <div style={{ color: "#8ecbff", fontSize: 13, marginTop: 2 }}>
          Cert: {String(user.certPoints).padStart(3, "0")} • Quiz:{" "}
          {String(user.quizPoints).padStart(3, "0")}
        </div>
      </div>
    );
  })}
</div>



        {/* ---------------- LIST BELOW (RANK 4+) ---------------- */}
        <div style={scrollArea}>
          {paginatedRows.map((p, i) => {
            const absoluteRank = 3 + (page - 1) * pageSize + (i + 1);

            return (
              <div key={p.employeeId} style={rowStyle}>
                {/* Left Section */}
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div style={{ width: 30, color: "#94d6ff", fontWeight: 700 }}>
                    #{absoluteRank}
                  </div>

                  <Avatar name={p.name} size={45} />

                  <div>
                    <div style={{ fontWeight: 700, fontSize: 16 }}>{p.name}</div>
                    <div style={{ fontSize: 12, color: "#8ecbff" }}>{p.location}</div>
                  </div>
                </div>

                {/* Right Section */}
                <div style={{ textAlign: "right" }}>
                  <div style={xpValue}>{String(p.totalXP).padStart(4, "0")} XP</div>
                  <div style={xpSub}>
                    Cert: {String(p.certPoints).padStart(3, "0")} • Quiz:{" "}
                    {String(p.quizPoints).padStart(3, "0")}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* ---------------- PAGINATION ---------------- */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: 20,
            padding: "0 5px",
          }}
        >
          <button
            className="btn btn-sm btn-outline-info"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            ◀ Previous
          </button>

          <div style={{ color: "#8ecbff", fontSize: 14 }}>
            Page {page} of {totalPages}
          </div>

          <button
            className="btn btn-sm btn-outline-info"
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
          >
            Next ▶
          </button>
        </div>
      </div>
    </div>
  );
}

/* ----------------------------- Styles ----------------------------- */

const cardContainer = {
  padding: 20,
  borderRadius: 18,
  background: "rgba(3,19,40,0.92)",
  border: "1px solid rgba(130,200,255,0.28)",
};

const titleStyle = {
  color: "#eaf6ff",
  marginBottom: 60,
  fontWeight: 700,
  fontSize: 22,
};

const scrollArea = {
  maxHeight: "60vh",
  overflowY: "auto",
  paddingRight: 6,
};

const rowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  background: "rgba(255,255,255,0.03)",
  borderRadius: 14,
  padding: "12px 16px",
  marginBottom: 10,
  border: "1px solid rgba(255,255,255,0.05)",
};

const xpValue = {
  fontSize: 18,
  fontWeight: 800,
  color: "#eaf6ff",
};

const xpSub = {
  color: "#8ecbff",
  fontSize: 12,
};
